import React from 'react';
import PropTypes from 'prop-types';
import CarouselV2 from '~shared/ui/CarouselV2/CarouselV2';
import WidgetHeader from '~shared/ui/WidgetHeader/WidgetHeader';
import { useLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useModal } from '~shared/components/modal-overlay/ModalContext';
import ArticleCard from './components/ArticleCard';
import ArticleModal from './components/ArticleModal';
import { getArticleImage, getDayHour } from './utils/utils';
import { useArticlesEnabled } from '~SpectateStore/hooks/clientStateHooks';
import { useLongArticleData } from './useLongArticleData';
import './styles/article-widget.scss';
import { MODAL_TYPES } from '~shared/components/modal-overlay/constants.js';
import WidgetContainer from '~shared/ui/WidgetContainer';

const BASE_CLASS = 'long-article';
const ARTICLE_TYPE = 'article';

const LongArticleEntry = ({
    sportSlug,
    eventId,
    gtmAction,
    gtmCategory,
    data,
}) => {
    const { lazyLoadIsEmpty } = useLazyLoadContext();
    const articlesEnabled = useArticlesEnabled();
    const articlesData = data || useLongArticleData({ sportSlug, eventId });

    if (!articlesEnabled || articlesData?.error) {
        lazyLoadIsEmpty();

        return null;
    }

    if (!articlesData?.article_data) {
        return null;
    }

    return <LongArticleWidget
        data={articlesData}
        eventId={eventId}
        gtmAction={gtmAction}
        gtmCategory={gtmCategory}
    />;
};

LongArticleEntry.propTypes = {
    eventId: PropTypes.string,
    gtmAction: PropTypes.string,
    gtmCategory: PropTypes.string,
    sportSlug: PropTypes.string,
    data: PropTypes.object,
};

LongArticleEntry.defaultProps = {
    eventId: '',
    gtmAction: '',
    gtmCategory: '',
    sportSlug: '',
    data: null,
};


const LongArticleWidget = ({
    eventId,
    gtmCategory,
    gtmAction,
    data,
}) => {
    const { lazyLoadHasLoaded } = useLazyLoadContext();
    const [ t ] = useTranslation();
    const { pushModal } = useModal();

    const longArticles = data.article_data;
    const isSingleLongArticle = data.article_data.length === 1;
    const hasEventId = eventId > 0;

    const openArticle = (clickedArticleId) => {
        pushModal(<ArticleModal articleId={clickedArticleId} gtmCategory={gtmCategory}/>, MODAL_TYPES.LONG_ARTICLE);
    };

    const buildArticles = () => {

        let articleBuild = [];
        if (longArticles) {
            articleBuild = longArticles.map(longArticle => buildArticle(longArticle));
        }

        return articleBuild;
    };

    const getHeader = () => {
        return (
            <WidgetHeader
                heading={t('long_articles_widget.header')}
                icon={ARTICLE_TYPE}
            />
        );
    };

    const buildArticle = (longArticle) => {
        const {
            author,
            event_name: eventName,
            id,
            image_filename: imageFilename,
            read_time: readTime,
            timediff,
            title,
        } = longArticle;

        const imageUrl = getArticleImage(imageFilename);
        const inlineImage = {
            backgroundImage: `url(${imageUrl})`,
        };

        const articlesClasses = classNames(`${BASE_CLASS}`, { [`${BASE_CLASS}--single-long-article`]: isSingleLongArticle });

        const timeDiffText = getDayHour(timediff, t);

        return (
            <div data-item-extra-info={id} className={articlesClasses} key={id} data-spectate-product={`${gtmCategory};${gtmAction};Open-${id}`}>
                <ArticleCard
                    title={title}
                    author={author}
                    timeDiffText={timeDiffText}
                    inlineImage={inlineImage}
                    readTime={readTime}
                    eventName={eventName}
                />
            </div>
        );
    };

    const carouselClass = classNames('long-articles',
        {
            'multiple-carousel-long-article': !isSingleLongArticle,
            'single-carousel-long-article': isSingleLongArticle,
        }
    );

    const eventClass = classNames({ [`${BASE_CLASS}-event`]: hasEventId });

    lazyLoadHasLoaded();

    return (
        <div className={eventClass}>
            <WidgetContainer>
                <CarouselV2
                    onItemClick={(clickedArticleId) => openArticle(clickedArticleId)}
                    shouldTriggerFirstClick={false}
                    jsxHeader={getHeader()}
                    jsxItems={buildArticles()}
                    extraClass={carouselClass}
                />
            </WidgetContainer>
        </div>
    );
};

LongArticleWidget.propTypes = {
    eventId: PropTypes.string,
    gtmAction: PropTypes.string,
    gtmCategory: PropTypes.string,
    data: PropTypes.object,
};

LongArticleWidget.defaultProps = {
    eventId: '',
    gtmAction: '',
    gtmCategory: '',
    data: {},
};

export default LongArticleEntry;
